import * as React from 'react';
import { forwardRef, ReactElement, Ref } from 'react';
import ExcelExportButton from 'components/common/file/ExcelExportButton';
import DataTableHeader from 'components/common/table/datatable/DataTableHeader';
import NewDataTableBody from 'components/common/table/datatable/NewDataTableBody';
import DataTableLayout from 'components/common/table/datatable/DataTableLayout';
import { groupListColumns } from 'components/groups/GroupListColumns';
import { CLOSETGroupItem, CLOSETGroupsPlanType } from '../../types/api/groups/closet';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

interface Props {
  data: CLOSETGroupItem[];
  totalPages: number;
  onChangePage: (pageIndex: number) => void;
  currentPage: number;
  isLoading: boolean;
  excelExportProps?: {
    exportData: CLOSETGroupItem[];
    exportLoading: boolean;
    onClickExport: () => void;
    fileName: string;
  };
}

const GroupListTable = forwardRef(
  (
    { totalPages, onChangePage, currentPage, data, excelExportProps, isLoading }: Props,
    ref: Ref<HTMLButtonElement>,
  ): ReactElement => {
    const groupListColumnData = groupListColumns(totalPages, currentPage);

    const rowStyle = (rowData: CLOSETGroupItem): CSSProperties => {
      if (rowData.planType === CLOSETGroupsPlanType.FREE || !rowData.subscriptionTerm.endDate) {
        return {};
      }
      const threeMonth = 3 * 30 * 24 * 60 * 60 * 1000;
      const subscriptionTermDate =
        new Date(rowData.subscriptionTerm.endDate).getTime() -
        new Date(rowData.subscriptionTerm.startDate).getTime();
      if (subscriptionTermDate >= threeMonth) {
        return {};
      }
      return {
        backgroundColor: 'rgba(255, 208, 0, 0.1)',
        '&:hover': {
          backgroundColor: 'rgba(255, 208, 0, 0.1) !important',
        },
      };
    };

    return (
      <DataTableLayout
        maxHeight={'60vh'}
        showPagination
        totalPages={totalPages}
        onChangePage={onChangePage}
        currentPageIndex={currentPage}
        upperButton={
          excelExportProps && (
            <ExcelExportButton
              ref={ref}
              width={100}
              buttonText={'Export Excel'}
              exportData={excelExportProps.exportData}
              fileName={excelExportProps.fileName}
              onClickExport={excelExportProps.onClickExport}
              exportLoading={excelExportProps.exportLoading}
            />
          )
        }
      >
        <DataTableHeader<CLOSETGroupItem> header={groupListColumnData} />
        <NewDataTableBody
          dataList={data ?? []}
          columns={groupListColumnData}
          loading={isLoading}
          rowStyle={rowStyle}
        />
      </DataTableLayout>
    );
  },
);

export default GroupListTable;
